import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Table,
  Upload,
  DatePicker,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AdminPromotionsService from "../../../Modules/Services/admin.promotions.service";
import API_BASE_URL from "../../../config";

const { TextArea } = Input;

const ManagePromotions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = await AdminPromotionsService.list();
      setRows(data);
    } catch (e) {
      message.error("Не удалось загрузить акции");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    if (editing) {
      form.setFieldsValue({
        description: editing.description,
        sort_order: editing.sort_order ?? 0,
        starts_at: editing.starts_at ? dayjs(editing.starts_at) : null,
        ends_at: editing.ends_at ? dayjs(editing.ends_at) : null,
      });
      setFileList([]);
    } else {
      form.resetFields();
      setFileList([]);
    }
  }, [editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const imageFile = fileList?.[0]?.originFileObj || null;
      const payload = {
        description: values.description,
        sortOrder: values.sort_order ?? 0,
        startsAt: values.starts_at ? values.starts_at.toISOString() : null,
        endsAt: values.ends_at ? values.ends_at.toISOString() : null,
        imageFile,
      };

      if (editing) {
        await AdminPromotionsService.update(editing.id, payload);
        message.success("Акция обновлена");
      } else {
        await AdminPromotionsService.create(payload);
        message.success("Акция создана");
      }

      setIsModalVisible(false);
      setEditing(null);
      fetchRows();
    } catch (e) {
      message.error("Проверьте поля формы");
    }
  };

  const handleDelete = async (id) => {
    try {
      await AdminPromotionsService.delete(id);
      message.success("Акция удалена");
      fetchRows();
    } catch (e) {
      message.error("Не удалось удалить акцию");
    }
  };

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "id", key: "id", width: 70 },
      {
        title: "Порядок",
        dataIndex: "sort_order",
        key: "sort_order",
        width: 90,
        sorter: (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        render: (v) => <span>{v ?? 0}</span>,
      },
      {
        title: "Картинка",
        dataIndex: "image_url",
        key: "image_url",
        width: 90,
        render: (image_url) =>
          image_url ? (
            <img
              src={`${API_BASE_URL}${image_url}`}
              alt=""
              style={{
                width: 56,
                height: 56,
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            />
          ) : (
            <span style={{ color: "rgba(0,0,0,0.35)" }}>—</span>
          ),
      },
      {
        title: "Описание",
        dataIndex: "description",
        key: "description",
        render: (t) => (
          <div style={{ maxWidth: 520, whiteSpace: "pre-wrap" }}>{t}</div>
        ),
      },
      {
        title: "Даты",
        key: "dates",
        width: 220,
        render: (_, r) => (
          <div style={{ fontSize: 12 }}>
            <div>С: {r.starts_at ? String(r.starts_at) : "—"}</div>
            <div>По: {r.ends_at ? String(r.ends_at) : "—"}</div>
          </div>
        ),
      },
      {
        title: "Действия",
        key: "actions",
        width: 220,
        render: (_, record) => (
          <>
            <Button
              type="primary"
              style={{ marginRight: 8 }}
              onClick={() => {
                setEditing(record);
                setIsModalVisible(true);
              }}
            >
              Редактировать
            </Button>
            <Popconfirm
              title="Удалить акцию?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button danger>Удалить</Button>
            </Popconfirm>
          </>
        ),
      },
    ],
    []
  );

  return (
    <div>
      <Button
        type="primary"
        style={{ marginBottom: 16 }}
        onClick={() => {
          setEditing(null);
          setIsModalVisible(true);
        }}
      >
        Создать акцию
      </Button>

      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={editing ? "Редактировать акцию" : "Создать акцию"}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => {
          setIsModalVisible(false);
          setEditing(null);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание акции" },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="Порядок (0 — внизу)"
            tooltip="1, 2, 3... — выше. 0 всегда уходит в самый низ списка."
          >
            <InputNumber style={{ width: "100%" }} min={0} step={1} />
          </Form.Item>

          <Form.Item name="starts_at" label="Действует с">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="ends_at" label="Действует по">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Картинка (jpg/png)">
            <Upload
              accept="image/png,image/jpeg"
              listType="picture"
              fileList={fileList}
              beforeUpload={() => false}
              maxCount={1}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              <Button icon={<UploadOutlined />}>Выбрать файл</Button>
            </Upload>
            {editing?.image_url && !fileList.length && (
              <div style={{ marginTop: 8, color: "rgba(0,0,0,0.55)" }}>
                Сейчас: <code>{editing.image_url}</code>
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManagePromotions;

